import React from 'react';
import PropTypes from 'prop-types';
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	HashRouter as Router,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactModal from 'react-modal';

import Login from './noauth/pages/login';
import ForgotPassword from './noauth/pages/forgot-password';
import ChangePassword from './noauth/pages/change-password';
import Layout from './auth/layout';
import TrackingBoard from './auth/pages/tracking-board';
import Campaigns from './auth/pages/campaigns';
import PreviousVisits from './auth/pages/previous';
import Profile from './auth/pages/profile';
import ProfilePic from './auth/pages/profile-pic';
import StartPage from './auth/pages/start-page';
import ADTNotificationPrefs from './auth/pages/adt-notification-prefs';
import SMSAlerts from './auth/pages/sms-alerts';
import Evisit from './auth/pages/evisit';
import SettingsPage from './auth/pages/settings';
import PatientNotifications from './auth/pages/patient-notifications';
import AcknowledgedNotifications from './auth/pages/acknowledged-notifications';
import Scheduling from './auth/pages/scheduling';
import TemplateBuilder from './auth/pages/template-builder';
import ADTViewer from './auth/pages/adt-viewer';
import AcrsSearch from './auth/pages/acrs-search';
import RPMData from './auth/pages/rpm-data';
import Logout from './auth/pages/logout';
import DischargeSummary from './auth/pages/discharge-summary';

ReactModal.setAppElement(document.getElementById('app'));
ReactModal.defaultStyles.overlay.zIndex = 2000;

function RequireAuth({ children, user }) {
	const location = useLocation();

	if (!user || !user.get('loggedIn')) {
		// Redirect them to the /login page, but save the current location they were
		// trying to go to when they were redirected. This allows us to send them
		// along to that page after they login, which is a nicer user experience
		// than dropping them off on the home page.
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	return children;
}

RequireAuth.propTypes = {
	user: PropTypes.object.isRequired,
	children: PropTypes.element,
};

const Main = ({ app }) => {
	const { user } = app;

	const showNotification = (options) => {
		const toastOptions = {};
		if (options?.timeout) {
			toastOptions.autoClose = options.timeout;
		}
		if (options.loading) {
			// returns toast id
			return toast.loading(options.message, toastOptions);
		}
		if (options.toastId) {
			toast.update(options.toastId, {
				...toastOptions,
				render: options.message,
				type: options.status,
				isLoading: false,
			});
		} else if (options.status === 'danger') {
			toast.error(options.message, toastOptions);
		} else if (options.status === 'success') {
			toast.success(options.message, toastOptions);
		} else if (options.status === 'warning') {
			toast.warn(options.message, toastOptions);
		} else {
			toast(options.message, toastOptions);
		}
	};

	return (
		<Router>
			<Routes>
				<Route path="/login" element={<Login user={user} />} />
				<Route
					path="/forgot-password"
					element={<ForgotPassword user={user} />}
				/>
				<Route path="/fpc" element={<ChangePassword user={user} />} />

				<Route element={<Layout user={user} isEvisit appSocket={app.socket} />}>
					<Route
						path="/evisit/:id"
						element={
							<RequireAuth user={user}>
								<Evisit
									user={user}
									showNotification={showNotification}
									appSocket={app.socket}
								/>
							</RequireAuth>
						}
					/>
				</Route>

				<Route element={<Layout user={user} appSocket={app.socket} />}>
					<Route
						path="/trackingboard/:page?"
						element={
							<RequireAuth user={user}>
								<TrackingBoard
									user={user}
									showNotification={showNotification}
								/>
							</RequireAuth>
						}
					/>
					<Route
						path="/previousevisits/:page?"
						element={
							<RequireAuth user={user}>
								<PreviousVisits user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/campaigns"
						element={
							<RequireAuth user={user}>
								<Campaigns user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/profile"
						element={
							<RequireAuth user={user}>
								<Profile user={user} showNotification={showNotification} />
							</RequireAuth>
						}
					/>
					<Route
						path="/profile/pic"
						element={
							<RequireAuth user={user}>
								<ProfilePic user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/profile/start-page"
						element={
							<RequireAuth user={user}>
								<StartPage user={user} showNotification={showNotification} />
							</RequireAuth>
						}
					/>
					<Route
						path="/profile/adt-notification-prefs"
						element={
							<RequireAuth user={user}>
								<ADTNotificationPrefs
									user={user}
									showNotification={showNotification}
								/>
							</RequireAuth>
						}
					/>
					<Route
						path="/profile/sms-alerts"
						element={
							<RequireAuth user={user}>
								<SMSAlerts user={user} showNotification={showNotification} />
							</RequireAuth>
						}
					/>
					<Route
						path="/settings/:tab?"
						element={
							<RequireAuth user={user}>
								<SettingsPage user={user} showNotification={showNotification} />
							</RequireAuth>
						}
					/>
					<Route
						path="/patientNotifications"
						element={
							<RequireAuth user={user}>
								<PatientNotifications user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/patientNotifications/acknowledged"
						element={
							<RequireAuth user={user}>
								<AcknowledgedNotifications user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/scheduling"
						element={
							<RequireAuth user={user}>
								<Scheduling user={user} showNotification={showNotification} />
							</RequireAuth>
						}
					/>
					<Route
						path="/scheduling/template-builder"
						element={
							<RequireAuth user={user}>
								<TemplateBuilder user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/patientsearch"
						element={
							<RequireAuth user={user}>
								<ADTViewer
									showPatientSearch
									user={user}
									showNotification={showNotification}
								/>
							</RequireAuth>
						}
					/>
					<Route
						path="/adtviewer"
						element={
							<RequireAuth user={user}>
								<ADTViewer user={user} showNotification={showNotification} />
							</RequireAuth>
						}
					/>
					<Route
						path="/acrssearch"
						element={
							<RequireAuth user={user}>
								<AcrsSearch user={user} />
							</RequireAuth>
						}
					/>
					<Route
						path="/rpmdata"
						element={
							<RequireAuth user={user}>
								<RPMData user={user} />
							</RequireAuth>
						}
					/>
				</Route>

				<Route
					path="/print/discharge-summary/:evisitId"
					element={
						<RequireAuth user={user}>
							<DischargeSummary />
						</RequireAuth>
					}
				/>

				<Route path="/logout" element={<Logout user={user} />} />

				<Route path="*" element={<Navigate to="/trackingboard" />} />
			</Routes>
		</Router>
	);
};

Main.propTypes = { app: PropTypes.object.isRequired };

export default Main;
